import { useTheme } from '@emotion/react';
import NextLink from 'next/link';
import PropTypes from 'prop-types';
import { forwardRef } from 'react';

import Box from 'shopper/components/Box';
import Flex from 'shopper/components/Flex';
import Heading from 'shopper/components/Heading';
import Price from 'shopper/components/Price';
import Text from 'shopper/components/Text';

import Anchor from 'components/Anchor';
import Image from 'components/Image';

import { isLastItem } from 'lib/array';
import { sendEvent } from 'lib/gtag';
import { getOfferImageLink } from 'lib/image';
import { toOfferLink } from 'lib/links';
import { toFormattedOfferOldPrice, toFormattedOfferPrice } from 'lib/offer';
import { sliceWithEllipsis } from 'lib/string';

import HEADING from 'constants/heading';

const onRelatedOfferClick = () =>
  sendEvent({ action: 'see_also', category: 'active' });

const RelatedOffers = forwardRef(({ offers, ...rest }, ref) => {
  const theme = useTheme();

  return (
    <Box ref={ref} {...rest}>
      <Flex
        alignItems="flex-start"
        flexDirection="column"
        pt={4}
        pb={[null, null, null, null, 4]}
        px={[4, null, 6]}
      >
        <Heading size="size5" as="span" mb={2}>
          {HEADING.PRODUCT_SUGGESTION}
        </Heading>
        <Flex
          flexWrap="wrap"
          flexDirection={['column', null, 'row', null, 'column']}
          full
        >
          {offers.map(
            (
              {
                offerId,
                offerPhoto,
                offerTitle,
                offerSlug,
                offerPrice,
                offerPriceType,
                offerOldPrice,
              },
              index
            ) => (
              <NextLink
                key={offerId}
                href={toOfferLink(offerSlug)}
                prefetch={false}
                passHref
              >
                <Anchor
                  as="a"
                  flexDirection="row"
                  display="flex"
                  py={4}
                  borderBottom={
                    !isLastItem(offers, index)
                      ? ['1px solid', null, 'none', null, '1px solid']
                      : null
                  }
                  borderColor={[
                    theme.app.commentItem.border,
                    null,
                    null,
                    null,
                    theme.app.commentItem.border,
                  ]}
                  width={[null, null, '50%', null, 'initial']}
                  onClick={onRelatedOfferClick}
                >
                  <Image
                    height={80}
                    width={80}
                    src={getOfferImageLink(offerPhoto, 80)}
                    alt={offerTitle}
                  />
                  <Flex flexDirection="column" ml={4}>
                    <Text fontSize={[12, null, 14]} mb={2}>
                      {sliceWithEllipsis(offerTitle.trim(), 54)}
                    </Text>
                    <Price
                      size="size2"
                      primaryInfo={toFormattedOfferPrice(
                        offerPrice,
                        offerPriceType
                      )}
                      secondaryInfo={toFormattedOfferOldPrice(
                        offerOldPrice,
                        offerPriceType
                      )}
                    />
                  </Flex>
                </Anchor>
              </NextLink>
            )
          )}
        </Flex>
      </Flex>
    </Box>
  );
});

RelatedOffers.displayName = 'RelatedOffers';

RelatedOffers.defaultProps = {
  offers: [],
};

RelatedOffers.propTypes = {
  offers: PropTypes.arrayOf(
    PropTypes.shape({
      offerId: PropTypes.number,
      offerLikes: PropTypes.number,
      offerOldPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      offerPhoto: PropTypes.string,
      offerPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
      offerTitle: PropTypes.string,
    })
  ),
};

export default RelatedOffers;
